import React, {
  ChangeEvent,
  KeyboardEvent,
  PureComponent,
  ReactNode
} from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import {
  BzitManufacturerMap,
  BzitSearchLocation,
  BzitSearchLocations,
  BzitVehicleSearch,
  SearchParams
} from '../../bzit';
import { FontIconSpan } from '../../components/FontIcon';
import { Column, Row } from '../../components/Grid';
import {
  Input,
  InputContainer,
  IntroSearchBar,
  Result,
  Results,
  ResultsContainer,
  ResultsScrollView,
  ResultType,
  SearchLink
} from '../../components/IntroSearch';
import { Section } from '../../components/Section';
import { AdditionalSearchParams } from '../../configs';
import {
  introSearchForm,
  IntroSearchForm
} from '../../configs/additionalSearchParams';
import { searchForm, SearchForm } from '../../configs/searchForm';
import { withErrorBoundary } from '../../error-handling/withErrorBoundary';
import ParseHtml from '../../helper/parser/htmlParser';
import {
  mkAdditionalSearchParams,
  mkSearchParams
} from '../../provider/SearchProvider';
import {
  SearchState
} from '../../provider/SearchProvider/types';
import {
  withSearch,
  WithSearchProps
} from '../../provider/SearchProvider/withSearch';
import { cancelledIntroSearch } from '../../provider/TrackingProvider/conversions/cancelledIntroSearch';
import { clickedIntroSearchResult } from '../../provider/TrackingProvider/conversions/clickedIntroSearchResult';
import {
  withTrackingApi,
  WithTrackingApiProps
} from '../../provider/TrackingProvider/withTrackingApi';
import {
  selectSearchMenuEntry,
  selectVehicleCentersMap
} from '../../provider/Typo3Provider/selectors';
import { Typo3State } from '../../provider/Typo3Provider/types';
import Typo3Label from '../../provider/Typo3Provider/Typo3Label';
import { getVehicleDetailPath } from '../../provider/Typo3Provider/utils';
import {
  withTypo3,
  WithTypo3Props
} from '../../provider/Typo3Provider/withTypo3';
import { Maybe } from '../../types';
import { SearchCombination } from './SearchCombination';

// hersteller -> search.searchParameters.nameManufacturer[hersteller]
// modelle -> search.searchParameters.nameManufacturer[hersteller].models
// aufbauarten -> search.searchParameters.nameManufacturer[hersteller].codeBody (translation)
// einsatzarten -> search.searchParameters.nameManufacturer[hersteller].codeUsage (translation)

export interface VehicleSearchData {
  displayValue: string;
  type: string;
  vehicleId: string;
}

type SearchResult = SearchCombination | VehicleSearchData | (BzitSearchLocation & {displayValue: string, type: 'location'});

interface QueryData {
  manufacturers: SearchCombination[];
  models: SearchCombination[];
  bodyTypes: SearchCombination[];
  usageTypes: SearchCombination[];
  locations: (BzitSearchLocation & {displayValue: string, type: 'location'})[];
  vehicles: VehicleSearchData[];
}

interface IntroSearchProps {
  mobile?: boolean;
  onClick?: () => void;
}

type SearchQuery = {
  rawQuery: string;
  normalizedQuery: string;
  searchWords: string[];
};

interface IntroSearchState {
  query: string;
  queryOnFocus: string;
  filteredQueryData: QueryData;
  queryData: QueryData;
  manufacturersMap: BzitManufacturerMap;
  focus: boolean;
  arrowSelectedResult: SearchResult | undefined;
  clickedResult: SearchResult | undefined;
}

const emptyQueryData = (): QueryData => ({
  manufacturers: [],
  models: [],
  bodyTypes: [],
  usageTypes: [],
  locations: [],
  vehicles: []
});

const processQuery = (
  manufacturersMap: BzitManufacturerMap,
  locations: BzitSearchLocations,
  search: SearchState,
  typo3: Typo3State
) => {
  const manufacturers: SearchCombination[] = Object.keys(manufacturersMap).map(
    m => new SearchCombination(m, '', '', 'manufacturer')
  );
  const models = ([] as SearchCombination[]).concat(
    ...manufacturers.map(({ manufacturer: m }) => {
      const manufacturer = manufacturersMap[m];
      const { models } = manufacturer || { models: [] };
      return SearchCombination.manufactureFromList(m, models, models, 'model');
    })
  );
  const bodyTypes = ([] as string[])
    .concat(
      ...manufacturers.map(({ manufacturer }) => {
        const m = manufacturersMap[manufacturer];
        return m ? m.codeBody : [];
      })
    )
    .filter((x, i, xs) => xs.indexOf(x) >= i)
    .map(
      bodyType =>
        new SearchCombination(
          '',
          bodyType,
          typo3.translateUsedVehicles(bodyType),
          'bodyType'
        )
    );
  const usageTypes = ([] as string[])
    .concat(
      ...manufacturers.map(({ manufacturer }) => {
        const m = manufacturersMap[manufacturer];
        return m ? m.codeUsage : [];
      })
    )
    .filter((x, i, xs) => xs.indexOf(x) >= i)
    .map(
      usageType =>
        new SearchCombination(
          '',
          usageType,
          typo3.translateUsedVehicles(usageType),
          'usageType'
        )
    );

  const vehicleCentersMap = selectVehicleCentersMap(typo3);
  const locationValues = Object.keys(locations)
    .map(key => locations[key] || [])
    .map((location: BzitSearchLocation[]) => {
      return Object.assign(
        {},
        ...location,
        ...location.map((location: BzitSearchLocation) => ({
          displayValue: location.name,
          type: 'location'
        }))
      );
    })
    .filter(location => {
      // use only in typo3 activated vehicle centers
      const center = vehicleCentersMap.get(location.code);
      return center && location;
    })
    .sort((a, b) => (a.name > b.name ? 1 : -1));

  // vehicle id
  const vehicles = search.allVehicles.map((vehicle: BzitVehicleSearch) => {
    return {
      displayValue: vehicle.vehicleId,
      type: 'vehicle',
      vehicleId: vehicle.vehicleId
    };
  });

  return {
    models,
    bodyTypes,
    usageTypes,
    manufacturers,
    locations: locationValues,
    vehicles
  };
};

const getSelectedResult = (
  queryData: QueryData,
  currentResult: any,
  direction: 1 | -1
) => {
  const {
    models,
    manufacturers,
    bodyTypes,
    usageTypes,
    locations,
    vehicles
  } = queryData;
  if (!currentResult) {
    return (
      manufacturers[0] ||
      models[0] ||
      bodyTypes[0] ||
      usageTypes[0] ||
      locations[0] ||
      vehicles[0]
    );
  } else {
    let lists = [
      manufacturers,
      models,
      bodyTypes,
      usageTypes,
      locations,
      vehicles
    ];
    // remove empty items from lists
    lists = lists.filter(item => item.length > 0);
    const currentList = lists.find(xs => xs.includes(currentResult));
    if (currentList) {
      let listIndex = lists.indexOf(currentList);
      let elementIndex = currentList.indexOf(currentResult) + direction;
      if (elementIndex < 0 || elementIndex >= currentList.length) {
        listIndex = (listIndex + direction + lists.length) % lists.length;
        elementIndex = elementIndex < 0 ? lists[listIndex].length - 1 : 0;
      }
      return lists[listIndex][elementIndex];
    }
  }
};

function normalizeSearchValue(value: string): string {
  return value.toLocaleLowerCase().replace(/[/+,;.:()[\]\\]+/gi, '-').replace(/-+/g, '-');
}

function parseSearchQuery(rawQuery: string): SearchQuery {
  const normalizedQuery = normalizeSearchValue(rawQuery);

  return {
    rawQuery,
    normalizedQuery,
    searchWords: normalizedQuery.split(' '),
  };
}

function applySearchQuery<T extends {displayValue: string}>(search: SearchQuery, list: T[]): T[] {
  return list
    .reduce<{result: T, score: number}[]>((agg, result) => {
      const normalizedDisplayValue = normalizeSearchValue(result.displayValue);
      let score = 0;
      if (normalizedDisplayValue.includes(search.normalizedQuery)) {
        score = 100;
      } else {
        // All search words must be included
        if (search.searchWords.every((searchWord) => normalizedDisplayValue.includes(searchWord))) {
          score = 10;
        }
      }

      if (score) {
        agg.push({result, score});
      }

      return agg;
    }, [])
    .sort((resultA, resultB) => resultB.score - resultA.score || resultA.result.displayValue.localeCompare(resultB.result.displayValue))
    .map((result) => result.result);
}

class IntroSearch extends PureComponent<
  IntroSearchProps &
    WithTypo3Props &
    WithSearchProps &
    RouteComponentProps &
    WithTrackingApiProps,
  IntroSearchState
> {
  public static getDerivedStateFromProps(
    { search, typo3 }: IntroSearchProps & WithTypo3Props & WithSearchProps,
    prevState: IntroSearchState
  ): Partial<IntroSearchState> | null {
    const { searchParameters } = search;
    const { nameManufacturer, locations } = searchParameters;
    const manufacturersChanged =
      nameManufacturer !== prevState.manufacturersMap;
    if (manufacturersChanged) {
      return {
        ...prevState,
        manufacturersMap: nameManufacturer,
        ...(manufacturersChanged
          ? {
              queryData: processQuery(
                nameManufacturer,
                locations,
                search,
                typo3
              )
            }
          : {})
      };
    }
    return null;
  }

  public resultsContainer: Maybe<HTMLElement> = null;

  public state: IntroSearchState = {
    query: '',
    queryOnFocus: '',
    filteredQueryData: emptyQueryData(),
    queryData: emptyQueryData(),
    manufacturersMap: {},
    focus: false,
    arrowSelectedResult: undefined,
    clickedResult: undefined,
  };

  public onQueryChange = (e: ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    if (!query) {
      this.setState({
        query,
        filteredQueryData: emptyQueryData(),
        arrowSelectedResult: undefined,
        clickedResult: undefined,
      });
      return;
    }

    const searchQuery = parseSearchQuery(query);

    this.setState(({ queryData }) => ({
      query,
      filteredQueryData: {
        manufacturers: applySearchQuery(searchQuery, queryData.manufacturers),
        models: applySearchQuery(searchQuery, queryData.models),
        bodyTypes: applySearchQuery(searchQuery, queryData.bodyTypes),
        usageTypes: applySearchQuery(searchQuery, queryData.usageTypes),
        locations: applySearchQuery(searchQuery, queryData.locations),
        vehicles: applySearchQuery(searchQuery, queryData.vehicles),
      }
    }));
  };

  private tryTrackingCancellation() {
    if (this.state.clickedResult || !this.state.query || this.state.query === this.state.queryOnFocus) {
      return;
    }

    const resultCount = Object.values(this.state.filteredQueryData).reduce((agg, cur) => {
      return agg + (cur && typeof cur.length === 'number' ? cur.length : 0)
    }, 0);

    this.props.trackingApi.trackEvents(cancelledIntroSearch(
      this.state.query,
      resultCount
    ));
  }

  public componentWillUnmount() {
    if (this.state.focus) {
      this.tryTrackingCancellation();
    }
  }

  private onWindowBeforeUnload = () => {
    this.tryTrackingCancellation();
  };

  public onFocus = () => {
    window.addEventListener('beforeunload', this.onWindowBeforeUnload);
    this.setState({
      focus: true,
      queryOnFocus: this.state.query,
    });
  };

  public onBlur = () => {
    window.removeEventListener('beforeunload', this.onWindowBeforeUnload);
    setTimeout(() => {
      // Timeout is needed because blur comes before selecting an element.
      // Therefore cancellation check wouldn't recognize that an element had been selected.
      this.tryTrackingCancellation();
    }, 250);

    this.setState({
      focus: false
    });
  };

  public onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const navigatesUp = e.keyCode === 40;
    const navigatesDown = e.keyCode === 38;
    const confirmed = e.keyCode === 13;
    const { query, arrowSelectedResult } = this.state;
    if (!query) {
      return;
    }

    if (navigatesUp || navigatesDown) {
      e.preventDefault();
      this.setState(
        ({ arrowSelectedResult: currentResult, filteredQueryData }) => {
          const arrowSelectedResult = getSelectedResult(
            filteredQueryData,
            currentResult,
            navigatesUp ? 1 : -1
          );
          return {
            arrowSelectedResult
          };
        }
      );
    } else if (confirmed && arrowSelectedResult) {
      this.onSelect(arrowSelectedResult);
    } else if (confirmed && !arrowSelectedResult) {
      // use search string
      this.onSubmit();
    }
  };

  public onSubmit = () => {
    const { history, mobile, onClick } = this.props;
    const { query } = this.state;
    if (query) {
      const searchLink = this.submitSearch(query);

      if (!searchLink) {
        return;
      }
      history.push(searchLink);
      if (mobile && onClick) {
        onClick();
      }
    }
  };

  public submitSearch(query: string) {
    const { filteredQueryData } = this.state;
    const { search } = this.props;
    const { setAdditionalSearchParams, setSearchFormData, formParams } = search;
    const {
      models,
      manufacturers,
      bodyTypes,
      usageTypes,
      locations,
      vehicles
    } = filteredQueryData;

    // check if there is exactly one match in vehicleId search, link to detail page
    const jumpToDetailPage =
      vehicles.length === 1 &&
      !models.length &&
      !manufacturers.length &&
      !bodyTypes.length &&
      !usageTypes.length &&
      !locations.length;
    if (jumpToDetailPage) {
      const vehicleId = vehicles[0].displayValue;
      return getVehicleDetailPath(this.props.typo3, vehicleId);
    } else {
      const { codeWheelpos, nameCurrency } = formParams;
      const mkFormData = (
        props: Partial<AdditionalSearchParams> = {}
      ): AdditionalSearchParams => ({
        ...introSearchForm,
        ...{ codeWheelpos, nameCurrency },
        ...props
      });

      // use all mentioned search params, search query string and link to search page
      const props = {
        namesManufacturer: manufacturers.map(
          manufacturer => manufacturer.manufacturer
        ), // manufacturer
        namesModel: models.map(model => model.value), // model
        codeBody: bodyTypes.map(bodyType => bodyType.value), // codeBody
        codeUsage: usageTypes.map(usageType => usageType.value), // codeUsage
        locations: locations.map(location => location.code), // codeLocation
        vehicleIds: vehicles.map(vehicle => vehicle.vehicleId) // vehicleId
      };

      let additionalSearchParams: IntroSearchForm;
      const additionalSearchProps = {
        ...props,
        queryString: query.trim()
      };

      additionalSearchParams = mkFormData(additionalSearchProps);
      setAdditionalSearchParams(additionalSearchParams);

      // remove search params, only use search query
      const searchParams = mkSearchParams({
        ...searchForm,
        ...{ codeWheelpos, nameCurrency }
      });
      setSearchFormData(searchParams);

      return this.getSearchLink();
    }
  }

  public getSearchLink() {
    const searchEntry = selectSearchMenuEntry(this.props.typo3);
    if (searchEntry) {
      return searchEntry.link;
    }
    return null;
  }

  public componentDidUpdate = (prevProps: IntroSearchProps, prevState: IntroSearchState) => {
    if (this.state.clickedResult && !prevState.clickedResult) {
      const {type, displayValue} = this.state.clickedResult;
      const {history, mobile, onClick} = this.props;

      this.props.trackingApi.trackEvents(clickedIntroSearchResult(
        type,
        displayValue,
        this.state.query
      ));

      if (type === 'vehicle') {
        const vehicleId = displayValue;
        const searchLink = getVehicleDetailPath(this.props.typo3, vehicleId);
        if (!searchLink) {
          return;
        }
        history.push(searchLink);
        if (mobile && onClick) {
          onClick();
        }
      } else {
        const formDataProps = this.getFormDataProps(this.state.clickedResult);
        this.onSelectResult(formDataProps);
      }
    }
  };

  public onSelect = (result: SearchResult) => {
    this.setState({clickedResult: result});
  };

  public onSelectResult = (formDataProps: Partial<SearchParams> = {}) => {
    const { search, history, mobile, onClick } = this.props;
    const { setAdditionalSearchParams, setSearchFormData } = search;
    const searchLink = this.getSearchLink();
    const { codeWheelpos, nameCurrency } = search.formParams;
    if (!searchLink) {
      return;
    }
    const mkFormData = (props: Partial<SearchParams> = {}): SearchParams => ({
      ...searchForm,
      ...{ codeWheelpos, nameCurrency },
      ...props
    });
    let formData: SearchForm;
    formData = mkFormData(formDataProps);
    setSearchFormData(formData);

    // remove search value
    const additionalSearchParams = mkAdditionalSearchParams(introSearchForm);
    setAdditionalSearchParams(additionalSearchParams);

    history.push(searchLink);

    if (mobile && onClick) {
      onClick();
    }
  };

  public setResultsContainer = (container: HTMLElement | null) => {
    this.resultsContainer = container;
  };

  public scrollIntoView = (element: HTMLElement | null) => {
    if (element && this.resultsContainer) {
      const { offsetTop, clientHeight } = element;
      this.resultsContainer.scrollTop = offsetTop - clientHeight;
    }
  };

  public getFormDataProps = (result: any) => {
    let formDataProps = {};
    switch (result.type) {
      case 'model':
        formDataProps = {
          namesModel: [result.value],
          namesManufacturer: [result.manufacturer]
        };
        break;
      case 'bodyType':
        formDataProps = {
          codeBody: result.value
        };
        break;
      case 'usageType':
        formDataProps = {
          codeUsage: result.value
        };
        break;
      case 'manufacturer':
        formDataProps = {
          namesManufacturer: [result.manufacturer]
        };
        break;
      case 'location':
        formDataProps = {
          codeLocation: result.code
        };
        break;
      default:
        formDataProps = {};
    }

    return formDataProps;
  };

  public renderResult = (result: any) => {
    const { displayValue } = result;
    const { arrowSelectedResult } = this.state;
    const isActive = arrowSelectedResult === result;
    const ref = isActive ? this.scrollIntoView : undefined;

    return (
      <Result
        key={displayValue}
        active={isActive}
        innerRef={ref}
        onClick={() => this.onSelect(result)}
      >
        <span>{ParseHtml(displayValue)}</span>
      </Result>
    );
  };

  public renderResultsGroup(title: ReactNode, group: any) {
    return (
      <React.Fragment>
        <ResultType>
          <span>{title}</span>
        </ResultType>
        <Results>{group.map(this.renderResult)}</Results>
      </React.Fragment>
    );
  }

  public renderResults() {
    const { filteredQueryData } = this.state;
    const {
      manufacturers,
      models,
      bodyTypes,
      usageTypes,
      locations,
      vehicles
    } = filteredQueryData;
    return (
      <ResultsContainer innerRef={this.setResultsContainer}>
        <ResultsScrollView>
          {manufacturers.length > 0 &&
            this.renderResultsGroup(
              <Typo3Label label="introSearch.label.manufacturer">
                Hersteller
              </Typo3Label>,
              manufacturers
            )}

          {models.length > 0 &&
            this.renderResultsGroup(
              <Typo3Label label="introSearch.label.models">Modelle</Typo3Label>,
              models
            )}

          {bodyTypes.length > 0 &&
            this.renderResultsGroup(
              <Typo3Label label="introSearch.label.composition">
                Aufbauarten
              </Typo3Label>,
              bodyTypes
            )}

          {usageTypes.length > 0 &&
            this.renderResultsGroup(
              <Typo3Label label="introSearch.label.usage">
                Einsatzarten
              </Typo3Label>,
              usageTypes
            )}

          {locations.length > 0 &&
            this.renderResultsGroup(
              <Typo3Label label="introSearch.label.locations">
                Standorte
              </Typo3Label>,
              locations
            )}

          {vehicles.length > 0 &&
            this.renderResultsGroup(
              <Typo3Label label="introSearch.label.vehicles">
                Fahrzeuge
              </Typo3Label>,
              vehicles
            )}
        </ResultsScrollView>
      </ResultsContainer>
    );
  }

  public render() {
    const { query, focus } = this.state;
    const { typo3 } = this.props;
    const active = focus || !!query;

    return (
      <IntroSearchBar className="hide-for-print">
        <Section>
          <Row justifyContent="center" noGap={true}>
            <Column large={8}>
              <InputContainer focus={active}>
                <Input
                  placeholder={typo3.translateTypo3(
                    'introSearch.placeholder.search',
                    null,
                    'Welche Hersteller, Modelle oder Einsatzarten suchen Sie?'
                  )}
                  value={query}
                  onChange={this.onQueryChange}
                  onFocus={this.onFocus}
                  onBlur={this.onBlur}
                  onKeyDown={this.onKeyDown}
                />
                <SearchLink onClick={this.onSubmit} secondary>
                  <FontIconSpan icon="search" />
                </SearchLink>
                {active && this.renderResults()}
              </InputContainer>
            </Column>
          </Row>
        </Section>
      </IntroSearchBar>
    );
  }
}

export default withErrorBoundary(
  withRouter(withTypo3(withTrackingApi(withSearch(IntroSearch)))),
  'IntroSearch'
);
